import Phaser from 'phaser';
import type {
  FriendListEntry,
  FriendsListResponse,
  UsernameSearchEntry,
} from '../../api/friends-types.js';
import { USERNAME_SEARCH_MIN_CHARS } from '../../api/friends-types.js';
import { getAuth } from '../../store/auth.js';
import {
  acceptFriendRequest,
  getFriends,
  onFriendsChange,
  refreshFriends,
  rejectFriendRequest,
  searchUsernames,
  sendFriendRequest,
  unfriend,
} from '../../store/friends.js';
import { attachBottomNav, bottomNavTopY, preloadNavAssets } from '../view/AppNav.js';
import { Button } from '../view/Button.js';
import { mountPageHeader } from '../view/PageHeader.js';
import { SectionTabs } from '../view/SectionTabs.js';
import { TextInputOverlay } from '../view/TextInputOverlay.js';
import { COLORS, FONT_HEADER, FONT_MONO } from '../style.js';
import { DESIGN_HEIGHT, DESIGN_WIDTH } from '../PhaserGame.js';

/**
 * v1.0 Phase B Slice 1 — Friends.
 *
 * Three sub-tabs, navigated via the shared SectionTabs component:
 *
 *   FRIENDS   — accepted friendships with an [Unfriend] action.
 *   REQUESTS  — incoming pending (Accept / Decline) + outgoing
 *               pending (Cancel).
 *   ADD       — username typeahead → Send request.
 *
 * Subscribes to the friends store on enter; refreshes its render on
 * every state change. Mutations go through the store wrappers, which
 * re-fetch on success so the cache + listeners stay in sync.
 */

const PAGE_BG_INT = 0x040713;
const SIDE_PADDING = 36;

const ROW_HEIGHT = 80;
const ROW_GAP = 10;

const SEARCH_DEBOUNCE_MS = 250;

type TabId = 'friends' | 'requests' | 'add';

export class FriendsScene extends Phaser.Scene {
  static readonly KEY = 'Friends';

  private activeTab: TabId = 'friends';
  private contentTop = 0;
  private content!: Phaser.GameObjects.Container;
  private tabContent!: Phaser.GameObjects.Container;
  private statusText: Phaser.GameObjects.Text | null = null;

  // ADD-tab state — text input is HTML, so it lives outside Phaser's
  // display list and needs explicit destroy on tab switch / shutdown.
  private addInput: TextInputOverlay | null = null;
  private addQuery = '';
  private addStatus = '';
  private addStatusColor: string = COLORS.textMuted;
  private searchTimer: ReturnType<typeof setTimeout> | null = null;
  private searchResults: readonly UsernameSearchEntry[] = [];
  private inFlight = false;

  // Buttons aren't children of tabContent (they self-mount on the
  // scene root) so wiping the container doesn't destroy them. Track
  // and destroy explicitly per render pass.
  private renderButtons: Button[] = [];

  // Same problem for the ADD tab's status text: it needs to sit at a
  // scene-root Y (just above the pinned send button) rather than be
  // shifted by tabContent's CONTENT_TOP offset. Track separately.
  private addStatusText: Phaser.GameObjects.Text | null = null;

  // Cleanup for the friends store subscription.
  private unsubscribe: (() => void) | null = null;

  constructor() {
    super(FriendsScene.KEY);
  }

  preload(): void {
    preloadNavAssets(this);
    this.load.image('friendsLogo', '/assets/logo2.png');
  }

  init(): void {
    // Reset per-instance state — scene.restart bypasses the
    // constructor.
    this.activeTab = 'friends';
    this.addQuery = '';
    this.addStatus = '';
    this.addStatusColor = COLORS.textMuted;
    this.searchResults = [];
    this.inFlight = false;
    this.addInput = null;
    this.searchTimer = null;
    this.statusText = null;
    this.addStatusText = null;
    this.renderButtons = [];
    this.unsubscribe = null;
  }

  create(): void {
    this.drawBackground();
    const { contentTop } = mountPageHeader({
      scene: this,
      logoAssetKey: 'friendsLogo',
      showBack: true,
    });
    this.contentTop = contentTop;
    this.content = this.add.container(0, contentTop);
    this.buildHeader();
    this.tabContent = this.add.container(0, 0);
    this.content.add(this.tabContent);

    attachBottomNav(this, 'friends');

    // Force a fresh fetch every time the scene opens — covers the
    // case where the user accepted a friend request in another tab
    // and the cache hadn't been updated yet.
    refreshFriends();

    // Subscribe to friends-store changes so the lists re-render
    // automatically when fetchFriends() resolves.
    this.unsubscribe = onFriendsChange(() => this.renderActiveTab());

    this.renderActiveTab();

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.cleanup());
  }

  private cleanup(): void {
    this.unsubscribe?.();
    this.unsubscribe = null;
    this.addInput?.destroy();
    this.addInput = null;
    this.addStatusText?.destroy();
    this.addStatusText = null;
    for (const b of this.renderButtons) b.destroy();
    this.renderButtons = [];
    if (this.searchTimer) {
      clearTimeout(this.searchTimer);
      this.searchTimer = null;
    }
  }

  // ----- Chrome -----

  private drawBackground(): void {
    const g = this.add.graphics();
    g.fillStyle(PAGE_BG_INT, 1);
    g.fillRect(0, 0, DESIGN_WIDTH, DESIGN_HEIGHT);
  }

  private buildHeader(): void {
    // Logo is mounted by mountPageHeader into the scene root; this
    // helper now only places the SectionTabs inside `this.content`,
    // butted directly against the static logo above.
    let y = 0;

    const tabs = new SectionTabs(this, {
      x: 0,
      y,
      width: DESIGN_WIDTH,
      items: [
        { id: 'friends', label: 'FRIENDS' },
        { id: 'requests', label: 'REQUESTS' },
        { id: 'add', label: 'ADD' },
      ],
      activeIndex: 0,
      onSelect: (id) => {
        if (id === 'friends' || id === 'requests' || id === 'add') {
          this.switchTab(id);
        }
      },
    });
    this.content.add(tabs.root);
  }

  // ----- Tab switching -----

  private switchTab(tab: TabId): void {
    if (tab === this.activeTab) return;
    // ADD tab owns the HTML input — tear it down when leaving so it
    // doesn't sit over the wrong tab's content.
    if (this.activeTab === 'add' && this.addInput) {
      this.addInput.destroy();
      this.addInput = null;
    }
    this.activeTab = tab;
    this.renderActiveTab();
  }

  private renderActiveTab(): void {
    // Wipe whatever was in the tab content container.
    this.tabContent.removeAll(true);
    this.statusText?.destroy();
    this.statusText = null;
    // Buttons live outside tabContent (Button mounts on scene root);
    // tear them down explicitly so old buttons don't survive a render.
    for (const b of this.renderButtons) b.destroy();
    this.renderButtons = [];
    this.addStatusText?.destroy();
    this.addStatusText = null;

    // Row content starts below the static page header (logo) and the
    // section tabs. mountPageHeader put the logo in scene root and
    // returned its bottom edge as `contentTop`; the tabs sit inside
    // `this.content` flush below it (~64px tall), then a small gap.
    const top = this.contentTop + 88; // tabs (~64) + gap (~24)

    const state = getFriends();
    if (state.kind === 'loading' || state.kind === 'unloaded') {
      this.renderStatus('Loading…', top);
      return;
    }
    if (state.kind === 'error') {
      this.renderStatus(`Couldn't load friends: ${state.error}`, top);
      return;
    }
    if (!getAuth().user) {
      this.renderStatus('Sign in to add friends.', top);
      return;
    }

    switch (this.activeTab) {
      case 'friends':
        this.renderFriendsTab(state.list, top);
        break;
      case 'requests':
        this.renderRequestsTab(state.list, top);
        break;
      case 'add':
        this.renderAddTab(top);
        break;
    }
  }

  private renderStatus(text: string, y: number): void {
    this.statusText = this.add
      .text(DESIGN_WIDTH / 2, y, text, {
        fontFamily: FONT_HEADER,
        fontSize: '22px',
        color: COLORS.textMuted,
      })
      .setLetterSpacing(2)
      .setOrigin(0.5, 0);
    this.tabContent.add(this.statusText);
  }

  // ----- FRIENDS tab -----

  private renderFriendsTab(
    list: FriendsListResponse,
    top: number
  ): void {
    if (list.accepted.length === 0) {
      this.renderStatus('No friends yet. Add one from the ADD tab.', top);
      return;
    }
    let y = top;
    for (const f of list.accepted) {
      this.drawFriendRow(f, y, 'unfriend');
      y += ROW_HEIGHT + ROW_GAP;
    }
  }

  private drawFriendRow(
    entry: FriendListEntry,
    top: number,
    actionKind: 'unfriend' | 'accept-decline' | 'cancel'
  ): void {
    const cx = DESIGN_WIDTH / 2;
    const w = DESIGN_WIDTH - SIDE_PADDING * 2;
    const bg = this.add
      .rectangle(cx, top + ROW_HEIGHT / 2, w, ROW_HEIGHT, COLORS.bgPanelInt)
      .setStrokeStyle(1, COLORS.borderSubtleInt, 0.6)
      .setOrigin(0.5);
    this.tabContent.add(bg);

    // Username on the left
    const label = this.add
      .text(SIDE_PADDING + 16, top + ROW_HEIGHT / 2, entry.username, {
        fontFamily: FONT_HEADER,
        fontSize: '26px',
        color: COLORS.textPrimary,
        fontStyle: '700',
      })
      .setLetterSpacing(1)
      .setOrigin(0, 0.5);
    this.tabContent.add(label);

    // Action buttons on the right. Positions computed from the row's
    // right edge (DESIGN_WIDTH - SIDE_PADDING) so buttons sit cleanly
    // INSIDE the row instead of overhanging it (which the original
    // hardcoded x values were doing — Decline used to extend 14px past
    // the row right edge at 720px design width).
    const rowRight = DESIGN_WIDTH - SIDE_PADDING;
    const innerPad = 16;
    if (actionKind === 'unfriend') {
      const btnW = 120;
      const btn = this.makeMiniButton(
        rowRight - innerPad - btnW / 2,
        top + ROW_HEIGHT / 2,
        btnW,
        'Unfriend',
        COLORS.accentRedHex,
        () => void this.handleUnfriend(entry.userId)
      );
      this.tabContent.add(btn);
    } else if (actionKind === 'accept-decline') {
      const btnW = 100;
      const btnGap = 12;
      const declineX = rowRight - innerPad - btnW / 2;
      const acceptX = declineX - btnW - btnGap;
      const accept = this.makeMiniButton(
        acceptX,
        top + ROW_HEIGHT / 2,
        btnW,
        'Accept',
        COLORS.accentCyanHex,
        () => void this.handleAccept(entry.userId)
      );
      const decline = this.makeMiniButton(
        declineX,
        top + ROW_HEIGHT / 2,
        btnW,
        'Decline',
        COLORS.textMuted,
        () => void this.handleReject(entry.userId)
      );
      this.tabContent.add(accept);
      this.tabContent.add(decline);
    } else {
      // cancel (outgoing pending)
      const btnW = 120;
      const btn = this.makeMiniButton(
        rowRight - innerPad - btnW / 2,
        top + ROW_HEIGHT / 2,
        btnW,
        'Cancel',
        COLORS.textMuted,
        () => void this.handleUnfriend(entry.userId)
      );
      this.tabContent.add(btn);
    }
  }

  private makeMiniButton(
    cx: number,
    cy: number,
    width: number,
    text: string,
    color: string,
    onTap: () => void
  ): Phaser.GameObjects.Container {
    const c = this.add.container(cx, cy);
    const bg = this.add
      .rectangle(0, 0, width, 52, COLORS.bgDeepInt)
      .setStrokeStyle(2, hexToInt(color), 0.85)
      .setInteractive({ useHandCursor: true });
    const label = this.add
      .text(0, 0, text, {
        fontFamily: FONT_HEADER,
        fontSize: '17px',
        color,
        fontStyle: '700',
      })
      .setLetterSpacing(1.5)
      .setOrigin(0.5);
    bg.on('pointerup', () => {
      if (this.inFlight) return;
      onTap();
    });
    bg.on('pointerover', () =>
      bg.setStrokeStyle(2, hexToInt(color), 1)
    );
    bg.on('pointerout', () =>
      bg.setStrokeStyle(2, hexToInt(color), 0.85)
    );
    c.add(bg);
    c.add(label);
    return c;
  }

  // ----- REQUESTS tab -----

  private renderRequestsTab(
    list: FriendsListResponse,
    top: number
  ): void {
    let y = top;

    // State banner — the page's job is to answer "does anyone want to
    // be my friend?". Surface that answer at a glance, big and bold,
    // before the per-direction sections so testers don't have to hunt
    // for a tiny "No pending requests." line.
    y = this.renderRequestsStateBanner(list.incoming.length, y);
    y += 32;

    y = this.renderRequestsSection(
      'INCOMING',
      list.incoming,
      y,
      'accept-decline',
      'No pending requests.'
    );
    y += 32;
    y = this.renderRequestsSection(
      'SENT',
      list.outgoing,
      y,
      'cancel',
      "You haven't sent any requests."
    );
  }

  private renderRequestsStateBanner(
    incomingCount: number,
    top: number
  ): number {
    const cx = DESIGN_WIDTH / 2;
    const bannerW = DESIGN_WIDTH - SIDE_PADDING * 2;
    const bannerH = 110;

    const hasRequests = incomingCount > 0;
    const message = hasRequests
      ? incomingCount === 1
        ? '1 PENDING REQUEST'
        : `${incomingCount} PENDING REQUESTS`
      : 'NO PENDING REQUESTS';
    const accentColor = hasRequests
      ? COLORS.accentCyanInt
      : COLORS.borderSubtleInt;
    const accentAlpha = hasRequests ? 1 : 0.5;
    const textColor = hasRequests
      ? COLORS.accentCyanHex
      : COLORS.textSecondary;

    const bg = this.add
      .rectangle(cx, top + bannerH / 2, bannerW, bannerH, COLORS.bgPanelInt)
      .setStrokeStyle(2, accentColor, accentAlpha)
      .setOrigin(0.5);
    this.tabContent.add(bg);

    const text = this.add
      .text(cx, top + bannerH / 2, message, {
        fontFamily: FONT_HEADER,
        fontSize: '30px',
        color: textColor,
        fontStyle: '700',
      })
      .setLetterSpacing(3)
      .setOrigin(0.5);
    this.tabContent.add(text);

    return top + bannerH;
  }

  private renderRequestsSection(
    title: string,
    entries: readonly FriendListEntry[],
    top: number,
    actionKind: 'accept-decline' | 'cancel',
    emptyText: string
  ): number {
    const headerY = top;
    const header = this.add
      .text(SIDE_PADDING, headerY, `> ${title}`, {
        fontFamily: FONT_MONO,
        fontSize: '22px',
        color: COLORS.accentCyanHex,
        fontStyle: '700',
      })
      .setLetterSpacing(1)
      .setOrigin(0, 0);
    this.tabContent.add(header);
    let y = headerY + header.height + 16;

    if (entries.length === 0) {
      const empty = this.add
        .text(SIDE_PADDING + 16, y, emptyText, {
          fontFamily: FONT_MONO,
          fontSize: '20px',
          color: COLORS.textMuted,
        })
        .setOrigin(0, 0);
      this.tabContent.add(empty);
      return y + empty.height + 4;
    }

    for (const e of entries) {
      this.drawFriendRow(e, y, actionKind);
      y += ROW_HEIGHT + ROW_GAP;
    }
    return y;
  }

  // ----- ADD tab -----

  private renderAddTab(top: number): void {
    // Label
    const labelY = top;
    const label = this.add
      .text(SIDE_PADDING, labelY, 'Find a player by username', {
        fontFamily: FONT_MONO,
        fontSize: '22px',
        color: COLORS.textSecondary,
      })
      .setOrigin(0, 0);
    this.tabContent.add(label);
    const inputY = labelY + label.height + 16;

    // HTML text input — mounted ONCE per tab visit, NOT recreated on
    // each re-render. Destroying + recreating the <input> on every
    // keystroke (e.g. when scheduleSearch fires a re-render to clear
    // stale results) removes the focused element from the DOM and
    // the browser drops focus — user had to re-click the input after
    // every letter typed. Keeping the input alive across re-renders
    // preserves focus. switchTab() + cleanup() handle the destroy
    // path on leave / shutdown.
    if (!this.addInput) {
      this.addInput = new TextInputOverlay({
        scene: this,
        x: DESIGN_WIDTH / 2,
        y: inputY + 32,
        width: DESIGN_WIDTH - SIDE_PADDING * 2,
        height: 64,
        type: 'text',
        placeholder: 'Username',
        value: this.addQuery,
        maxLength: 20,
        fontSizeDesign: 22,
        onChange: (v) => {
          this.addQuery = v;
          this.scheduleSearch(v);
        },
        onSubmit: () => {
          if (this.addQuery.trim().length > 0) {
            void this.handleSend(this.addQuery.trim());
          }
        },
      });
    }

    // Send button — pinned to a fixed Y just above the bottom nav so
    // the matches list never crowds it from underneath. Status text
    // sits immediately above the button; the results fill the space
    // between the input and the status row.
    //
    // Capped at SEARCH_LIMIT (5) results so at 44px/row this region
    // tops out around 220px — easily fits the ~500px gap on a 720×1280
    // canvas without scrolling.
    const buttonHeight = 64;
    const buttonGapFromNav = 24;
    const buttonCenterY =
      bottomNavTopY() - buttonGapFromNav - buttonHeight / 2;
    const buttonTopY = buttonCenterY - buttonHeight / 2;

    // Search results dropdown (Phaser-rendered chips below the input).
    // Rows are 64px tall with 80px between them so each username
    // reads at a glance on a phone instead of being a thin strip.
    const dropdownY = inputY + 80;
    let resultY = dropdownY;
    if (this.searchResults.length > 0) {
      for (const r of this.searchResults) {
        this.drawSearchResultRow(r, resultY);
        resultY += 80;
      }
    } else if (this.addQuery.trim().length >= USERNAME_SEARCH_MIN_CHARS) {
      const noResults = this.add
        .text(SIDE_PADDING + 16, resultY, 'No matches.', {
          fontFamily: FONT_MONO,
          fontSize: '22px',
          color: COLORS.textMuted,
        })
        .setOrigin(0, 0);
      this.tabContent.add(noResults);
      resultY += noResults.height;
    }

    // Status line sits just above the pinned button. Mounted on the
    // scene root (NOT tabContent) so its Y is direct scene coords and
    // lines up with the button — tabContent's CONTENT_TOP offset would
    // otherwise push it well below where the button is rendered.
    if (this.addStatus) {
      this.addStatusText = this.add
        .text(DESIGN_WIDTH / 2, buttonTopY - 28, this.addStatus, {
          fontFamily: FONT_MONO,
          fontSize: '24px',
          color: this.addStatusColor,
          fontStyle: '700',
        })
        .setLetterSpacing(1)
        .setOrigin(0.5, 1);
    }

    const sendButton = new Button(this, {
      x: DESIGN_WIDTH / 2,
      y: buttonCenterY,
      width: DESIGN_WIDTH - SIDE_PADDING * 2,
      height: buttonHeight,
      text: this.inFlight ? 'Sending…' : 'Send friend request',
      style: 'primary',
      onTap: () => {
        if (this.addQuery.trim().length === 0) return;
        void this.handleSend(this.addQuery.trim());
      },
    });
    this.renderButtons.push(sendButton);
  }

  private drawSearchResultRow(
    entry: UsernameSearchEntry,
    top: number
  ): void {
    const cx = DESIGN_WIDTH / 2;
    const w = DESIGN_WIDTH - SIDE_PADDING * 2;
    const rowHeight = 64;
    const centerY = top + rowHeight / 2;
    const bg = this.add
      .rectangle(cx, centerY, w, rowHeight, COLORS.bgPanelInt)
      .setStrokeStyle(1, COLORS.accentCyanInt, 0.5)
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });
    const text = this.add
      .text(SIDE_PADDING + 16, centerY, entry.username, {
        fontFamily: FONT_MONO,
        fontSize: '28px',
        color: COLORS.textPrimary,
        fontStyle: '700',
      })
      .setLetterSpacing(1)
      .setOrigin(0, 0.5);
    bg.on('pointerup', () => {
      // Fill the input from the clicked result, but KEEP the row
      // visible — the user just confirmed which player they want, so
      // wiping the dropdown (and flashing "No matches." while we wait
      // for the next API call) is the opposite of helpful. The row
      // stays, framing the selection until the next keystroke.
      this.addQuery = entry.username;
      this.addInput?.setValue(entry.username);
      this.searchResults = [entry];
      this.renderActiveTab();
    });
    bg.on('pointerover', () =>
      bg.setStrokeStyle(1, COLORS.accentCyanInt, 1)
    );
    bg.on('pointerout', () =>
      bg.setStrokeStyle(1, COLORS.accentCyanInt, 0.5)
    );
    this.tabContent.add(bg);
    this.tabContent.add(text);
  }

  private scheduleSearch(value: string): void {
    if (this.searchTimer) clearTimeout(this.searchTimer);
    const trimmed = value.trim();
    if (trimmed.length < USERNAME_SEARCH_MIN_CHARS) {
      // Clear any stale results from a previous longer query and
      // refresh the dropdown / "No matches" state. The Phaser chrome
      // around the input churns on every keystroke, but the input
      // itself is now persistent across renders (see renderAddTab)
      // so focus is preserved.
      this.searchResults = [];
      this.renderActiveTab();
      return;
    }
    this.searchTimer = setTimeout(() => {
      void this.runSearch(trimmed);
    }, SEARCH_DEBOUNCE_MS);
  }

  private async runSearch(prefix: string): Promise<void> {
    const result = await searchUsernames(prefix);
    if (!this.scene.isActive()) return;
    // Defensive: ignore stale results from a previous query.
    if (this.addQuery.trim() !== prefix) return;
    this.searchResults = result?.results ?? [];
    this.renderActiveTab();
  }

  private async handleSend(username: string): Promise<void> {
    if (this.inFlight) return;
    this.inFlight = true;
    this.addStatus = '';
    this.renderActiveTab();
    const result = await sendFriendRequest(username);
    if (!this.scene.isActive()) return;
    this.inFlight = false;
    if (result.ok) {
      this.addStatus = 'Friend request sent.';
      this.addStatusColor = COLORS.accentCyanHex;
      this.addQuery = '';
      this.searchResults = [];
    } else {
      this.addStatus = result.error;
      this.addStatusColor = COLORS.accentRedHex;
    }
    this.renderActiveTab();
  }

  // ----- Row actions -----

  private async handleAccept(otherUserId: string): Promise<void> {
    if (this.inFlight) return;
    this.inFlight = true;
    await acceptFriendRequest(otherUserId);
    this.inFlight = false;
    // Listener fires renderActiveTab automatically.
  }

  private async handleReject(otherUserId: string): Promise<void> {
    if (this.inFlight) return;
    this.inFlight = true;
    await rejectFriendRequest(otherUserId);
    this.inFlight = false;
  }

  private async handleUnfriend(otherUserId: string): Promise<void> {
    if (this.inFlight) return;
    this.inFlight = true;
    await unfriend(otherUserId);
    this.inFlight = false;
  }

}

function hexToInt(hex: string): number {
  // Strip leading "#" and parse as base-16. Used so the hex strings
  // in COLORS work with Phaser's int-only fill/stroke APIs.
  return parseInt(hex.replace(/^#/, ''), 16);
}
